import React, { useState, useEffect } from "react";
import { toast } from "sonner";
import { X, Plus, Trash2, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { inventoryApi } from "./inventoryApi";
import { REQUEST_PRIORITIES } from "./procurementConstants";

const emptyLine = () => ({ part_id: "", part_number: "", description: "", quantity: 1, core_required: false, notes: "" });

export const RequestFormModal = ({ open, onClose, vehicles, currentUser, onSaved }) => {
  const [unitId, setUnitId] = useState("");
  const [priority, setPriority] = useState("scheduled_repair");
  const [description, setDescription] = useState("");
  const [notes, setNotes] = useState("");
  const [lines, setLines] = useState([emptyLine()]);
  const [parts, setParts] = useState([]);
  const [saving, setSaving] = useState(false);
  const userName = currentUser ? `${currentUser.first_name} ${currentUser.last_name}` : "Unknown";

  useEffect(() => {
    if (open) {
      setUnitId(""); setPriority("scheduled_repair"); setDescription(""); setNotes(""); setLines([emptyLine()]);
      inventoryApi.getParts().then(setParts).catch(() => {});
    }
  }, [open]);

  const setLine = (i, k, v) => setLines((ls) => ls.map((l, idx) => (idx === i ? { ...l, [k]: v } : l)));
  const onPartSelect = (i, partId) => {
    const p = parts.find((x) => x.id === partId);
    setLines((ls) => ls.map((l, idx) => idx === i ? { ...l, part_id: partId, part_number: p?.part_number || "", description: l.description || p?.name || "" } : l));
  };

  const submit = async () => {
    const validLines = lines.filter((l) => l.description?.trim());
    if (validLines.length === 0) { toast.error("Add at least one line item with a description"); return; }
    const unit = vehicles.find((v) => v.id === unitId);
    setSaving(true);
    try {
      await inventoryApi.createRequest({
        unit_type: unit?.vehicle_type, unit_id: unitId || null, unit_number: unit?.unit_number,
        priority, description, notes,
        line_items: validLines.map((l) => ({
          part_id: l.part_id || null, part_number: l.part_number || null,
          description: l.description, quantity: Number(l.quantity) || 1,
          core_required: !!l.core_required, notes: l.notes || null,
        })),
        requested_by: userName, requested_by_id: currentUser?.id,
      });
      toast.success("Request submitted");
      onSaved(); onClose();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to create request"); }
    setSaving(false);
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl p-0 gap-0 h-[100dvh] sm:h-auto sm:max-h-[90vh] flex flex-col" hideClose data-testid="request-form-modal">
        <DialogTitle className="sr-only">New Parts Request</DialogTitle>
        <DialogDescription className="sr-only">Create a multi-line parts request</DialogDescription>
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200 flex-shrink-0">
          <h2 className="text-lg font-semibold text-slate-900">New Parts Request</h2>
          <button onClick={onClose} className="p-2 hover:bg-slate-100 rounded-lg"><X className="w-5 h-5 text-slate-500" /></button>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <Label className="text-xs text-slate-500 mb-1 block">Unit</Label>
              <select className="w-full h-10 px-3 rounded-md border border-slate-200 text-sm" value={unitId} onChange={(e) => setUnitId(e.target.value)} data-testid="request-unit-select">
                <option value="">Select unit...</option>
                {vehicles.map((v) => <option key={v.id} value={v.id}>Unit {v.unit_number} ({v.vehicle_type?.replace("_", " ")})</option>)}
              </select>
            </div>
            <div>
              <Label className="text-xs text-slate-500 mb-1 block">Priority</Label>
              <select className="w-full h-10 px-3 rounded-md border border-slate-200 text-sm" value={priority} onChange={(e) => setPriority(e.target.value)} data-testid="request-priority-select">
                {REQUEST_PRIORITIES.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
              </select>
            </div>
          </div>
          <div>
            <Label className="text-xs text-slate-500 mb-1 block">Description of Issue</Label>
            <Textarea rows={2} value={description} onChange={(e) => setDescription(e.target.value)} placeholder="What's wrong?" />
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <Label className="text-sm font-semibold text-slate-800">Line Items</Label>
              <Button type="button" variant="outline" size="sm" onClick={() => setLines((ls) => [...ls, emptyLine()])} data-testid="add-line-btn"><Plus className="w-3.5 h-3.5 mr-1" /> Add Line</Button>
            </div>
            <div className="space-y-3">
              {lines.map((l, i) => (
                <div key={i} className="border border-slate-200 rounded-lg p-3 space-y-2" data-testid={`request-line-${i}`}>
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-medium text-slate-500">Line {i + 1}</span>
                    {lines.length > 1 && <button onClick={() => setLines((ls) => ls.filter((_, idx) => idx !== i))} className="text-slate-400 hover:text-red-500"><Trash2 className="w-4 h-4" /></button>}
                  </div>
                  <select className="w-full h-9 px-2 rounded-md border border-slate-200 text-sm" value={l.part_id} onChange={(e) => onPartSelect(i, e.target.value)}>
                    <option value="">Link inventory part (optional)...</option>
                    {parts.map((p) => <option key={p.id} value={p.id}>{p.part_number} — {p.name} (avail {p.quantity_available})</option>)}
                  </select>
                  <div className="grid grid-cols-3 gap-2">
                    <Input className="col-span-2" placeholder="Description *" value={l.description} onChange={(e) => setLine(i, "description", e.target.value)} data-testid={`line-desc-${i}`} />
                    <Input type="number" min="1" placeholder="Qty" value={l.quantity} onChange={(e) => setLine(i, "quantity", e.target.value)} data-testid={`line-qty-${i}`} />
                  </div>
                  <label className="flex items-center gap-2 text-xs text-slate-600">
                    <input type="checkbox" checked={l.core_required} onChange={(e) => setLine(i, "core_required", e.target.checked)} /> Core required
                  </label>
                </div>
              ))}
            </div>
          </div>

          <div>
            <Label className="text-xs text-slate-500 mb-1 block">Notes</Label>
            <Textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} />
          </div>
        </div>

        <div className="flex gap-3 px-5 py-4 border-t border-slate-200 flex-shrink-0">
          <Button variant="outline" className="flex-1" onClick={onClose}>Cancel</Button>
          <Button className="flex-1" onClick={submit} disabled={saving} data-testid="request-submit">{saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Submit Request"}</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
